import moment, {Moment} from 'moment';
import {getid} from '../../utils/getid';
import {
    Group,
    GroupBooking,
    GroupResourceOption,
    RoomParty,
    RoomPartyMember,
    RoomPartyMemberOption,
    RoomPartyOption
} from '../../graphql/groups.datatypes';
import {
    ResourceOption,
    ResourceOptionAction,
    ResourceOptionConditionType,
    ResourceOptionTarget
} from '../../graphql/resources.datatypes';
import {conditions} from '../../graphql/common.datatypes';
import _ from 'underscore';
import {BillingParty} from '../../graphql/billing_party.datatypes';

export interface OptionValue {
    id?: number
    optionId: number
    value: number
    adminEdited: boolean
    groupResourceOption: GroupResourceOption
}

export interface GuestData {
    id: string
    first_name: string
    last_name: string
    dob: string
    tour_role: number
    primary_occupant: boolean
    isValid: boolean
    roomGuestNumber: number

    billingPartyId: string
    roomId: string
    optionValues: OptionValue[]
}

export interface RoomData {
    id: string
    guestsData: GuestData[]
    wheelchair: boolean,
    hasInfants: boolean,
    notes: string
    isValid: boolean
    optionValues: OptionValue[]
    hotelName: string
}

export interface BillingPartyData {
    id: string
    first_name: string
    last_name: string
    email: string
    address_line_1: string
    address_line_2: string
    city: string
    state: string
    zipcode: string
    country: string
    primary_phone: string
    primary_phone_code: string
    secondary_phone: string
    secondary_phone_code: string
    onlyParty: boolean
    isValid: boolean
}

export interface BookingData {
    id: number
    roomsData: RoomData[]
    billingPartiesData: BillingPartyData[]
    nights: {
        start: moment.Moment
        end: moment.Moment
        mandatoryStart: moment.Moment
        mandatoryEnd: moment.Moment
    }
    validData: boolean[]
}

export const newGuestData = (roomId: string, billingPartyId: string) => {
    return {
        id: getid(),
        first_name: '',
        last_name: '',
        dob: moment(new Date()).format('YYYY/MM/DD'),
        tour_role: -1,
        primary_occupant: false,
        isValid: false,
        roomId,
        billingPartyId,
        optionValues: [],
        roomGuestNumber: 1,
    };
};

export const newRoomData = (billingPartyId: string, hotelName: string) => {
    const id = getid();
    const guestData = newGuestData(id, billingPartyId);
    guestData.primary_occupant = true;

    return {
        id,
        guestsData: [newGuestData(id, billingPartyId)],
        wheelchair: false,
        hasInfants: false,
        notes: '',
        isValid: false,
        optionValues: [],
        hotelName,
    };
};

export const newBillingPartyData = (onlyParty: boolean = false) => {
    return {
        id: getid(),
        first_name: '',
        last_name: '',
        email: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zipcode: '',
        country: '',
        primary_phone: '',
        primary_phone_code: '',
        secondary_phone: '',
        secondary_phone_code: '',
        isValid: false,
        onlyParty,
    };
};

export const newBookingData = (group: Group) => {
    const billingPartiesData = [newBillingPartyData()];
    const roomsData = [newRoomData('', group.hotel!.internal_name!)];

    return {
        id: -1,
        roomsData,
        billingPartiesData,
        nights: {
            start: moment(group.start_date).clone(),
            end: moment(group.end_date).clone(),
            mandatoryStart: moment(group.start_date).clone(),
            mandatoryEnd: moment(group.end_date).clone(),
        },
        validData: [true, true, false, true, false, false]
    };
};

export const getTotalBookingCost = (group: Group, bookingData: BookingData, allGuestData: GuestData[]) => {
    const guestsCosts = allGuestData
        .map(guestData => getTotalGuestCost(group, bookingData, guestData));

    return guestsCosts.reduce((accum: number, value: number) => accum + value, 0);
};

export const getTotalBillingPartyCost = (group: Group, billingParty: BillingPartyData, bookingData: BookingData, allGuestData: GuestData[]) => {
    const guestsCosts = allGuestData
        .filter(guestData => guestData.billingPartyId === billingParty.id)
        .map(guestData => getTotalGuestCost(group, bookingData, guestData));

    return guestsCosts.reduce((accum: number, value: number) => accum + value, 0);
};

export const getOptionDetails = (option: GroupResourceOption): { title: string, cost: number } | null => {
    if (option.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER) return {
        title: option.option.display_name!,
        cost: Number(option.unitCost)
    };
    if (option.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER_PER_NIGHT) return {
        title: option.option.display_name!,
        cost: Number(option.unitCost)
    };
    if (option.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_MANDATORY) return {
        title: option.option.resource!.display_name,
        cost: Number(option.unitCost)
    };
    if (option.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_AVAILABLE) return {
        title: option.option.resource!.display_name,
        cost: Number(option.unitCost)
    };
    return null;
};

export const getGuestBaseCost = (group: Group, bookingData: BookingData, guestData: GuestData) => {
    let baseCost = 0;
    baseCost += group.convenience_fee ? group.convenience_fee : 0;
    baseCost += guestData.optionValues.reduce((value, optionValue) => {
        const groupOption = group.options!.find(checkOption => optionValue.optionId === checkOption.option.id)!;
        if (groupOption.is_hidden) {
            return value + (Number(groupOption.unitCost) * optionValue.value);
        }
        return value;
    }, 0);
    return baseCost;
};

export const getTotalGuestCost = (group: Group, bookingData: BookingData, guestData: GuestData) => {
    let totalCost = 0;
    totalCost += group.convenience_fee ? group.convenience_fee : 0;
    totalCost += guestData.optionValues.reduce((value, optionValue) => {
        const groupOption = group.options!.find(checkOption => optionValue.optionId === checkOption.option.id)!;
        return value + (Number(groupOption.unitCost) * optionValue.value);
    }, 0);
    return totalCost;
};

export const getRoomsDataOptions = (group: Group, bookingData: BookingData, roomsData: RoomData[]) => {
    return roomsData.map(roomData => {
        const roomMandatory = getMandatoryRoomOptions(group, bookingData, roomData);
        const roomOptions = [...getAvailableRoomOptions(group, bookingData, roomData), ...roomMandatory];

        let roomOptionValues: OptionValue[] = [];
        //Remove options from room that are no longer valid
        roomOptionValues = roomData.optionValues.filter(optionValue => roomOptions.find(option => option.option.id === optionValue.optionId) !== undefined);
        //Add in new options that are not present.
        roomOptionValues = [...roomOptionValues, ...roomOptions.filter(option => roomOptionValues.find(optionValue => optionValue.optionId === option.option.id) === undefined).map(option => ({
            optionId: option.option.id!,
            value: roomMandatory.indexOf(option) === -1 ? 0 : 1,
            groupResourceOption: option,
            adminEdited: false,
        }))];

        const updatedRoomData = {...roomData, optionValues: roomOptionValues};
        const guestsData = updatedRoomData.guestsData.map(guestData => {
            const guestMandatory = getMandatoryTravellerOptions(group, bookingData, updatedRoomData, guestData);
            const guestOptions = [...getAvailableTravellerOptions(group, bookingData, updatedRoomData, guestData), ...guestMandatory];

            let guestOptionValues: OptionValue[] = [];
            //Remove options from room that are no longer valid
            guestOptionValues = guestData.optionValues
                .filter(optionValue => guestOptions.find(option => option.option.id === optionValue.optionId) !== undefined)
                .filter(optionValue => optionValue.groupResourceOption.action_type !== ResourceOptionAction.ADD_COST_TO_TRAVELLER_PER_NIGHT);
            //Add in new options that are not present.
            guestOptionValues = [...guestOptionValues, ...guestOptions.filter(option => guestOptionValues.find(optionValue => optionValue.optionId === option.option.id) === undefined).map(option => ({
                optionId: option.option.id!,
                value: guestMandatory.indexOf(option) === -1 ? 0 :
                    option.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER_PER_NIGHT ? getOptionCountForTravellerPerNight(group, option.option, bookingData, roomData, guestData) : 1,
                groupResourceOption: option,
                adminEdited: false,
            }))];
            //console.log(guestOptionValues.map(option => ({ internal_name: option.groupResourceOption.option.internal_name, value: option.value  })));
            return {...guestData, optionValues: guestOptionValues}
        });

        return {...updatedRoomData, guestsData}
    });
}

export const getAvailableRoomOptions = (group: Group, booking: BookingData, room: RoomData) => group.options!
    .filter(groupOption =>
        !groupOption.option.disabled &&
        groupOption.option.target === ResourceOptionTarget.ROOMS &&
        groupOption.action_type === ResourceOptionAction.ROOM_RESOURCE_AVAILABLE &&
        checkOptionRules(group, groupOption.option, booking, room),
    )
    .sort((a, b) => a.id - b.id);

export const getMandatoryRoomOptions = (group: Group, booking: BookingData, room: RoomData) => group.options!
    .filter(groupOption =>
        !groupOption.option.disabled &&
        groupOption.option.target === ResourceOptionTarget.ROOMS &&
        (
            groupOption.action_type === ResourceOptionAction.ROOM_RESOURCE_MANDATORY
        ) &&
        checkOptionRules(group, groupOption.option, booking, room),
    )
    .sort((a, b) => a.id - b.id);

export const getAvailableTravellerOptions = (group: Group, booking: BookingData, room: RoomData, traveller?: GuestData) => {
    return group.options!
        .filter(groupOption =>
            !groupOption.option.disabled &&
            groupOption.option.target === ResourceOptionTarget.TRAVELLERS &&
            groupOption.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_AVAILABLE &&
            checkOptionRules(group, groupOption.option, booking, room, traveller)
        )
        .sort((a, b) => a.id - b.id);
}

export const getMandatoryTravellerOptions = (group: Group, booking: BookingData, room: RoomData, traveller?: GuestData) => {
    const options = group.options!
        .filter(groupOption =>
            !groupOption.option.disabled &&
            groupOption.option.target === ResourceOptionTarget.TRAVELLERS &&
            (
                groupOption.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_MANDATORY ||
                groupOption.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER
            ) &&
            checkOptionRules(group, groupOption.option, booking, room, traveller,null,  groupOption)
        );

    const perNightOptions = group.options!
        .filter(groupOption =>
            !groupOption.option.disabled &&
            groupOption.option.target === ResourceOptionTarget.TRAVELLERS &&
            groupOption.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER_PER_NIGHT &&
            getOptionCountForTravellerPerNight(group, groupOption.option, booking, room, traveller) > 0
        );

    return [...options, ...perNightOptions].sort((a, b) => a.id - b.id);
}

export const getOptionCountForTravellerPerNight = (group: Group, option: ResourceOption, booking: BookingData, room?: RoomData, traveller?: GuestData) => {
    let unitCount = 0;
    const bookingLength = booking.nights.end.diff(booking.nights.start, 'days');

    for (let i = 0; i < bookingLength; i++) {
        const roomPartyNight = booking.nights.start.clone().add(i, 'days');
        if (checkOptionRules(group, option, booking, room, traveller, roomPartyNight)) {
            unitCount += 1;
        }
    }
    return unitCount;
};

export const getMemberAgeAtEvent = (booking: BookingData, traveller: GuestData) => {
    const dob = moment(new Date(traveller!.dob));
    return getAgeAtEvent(booking, dob);
};

export const getAgeAtEvent = (booking: BookingData, dob: Moment) => {
    return booking.nights.start.diff(dob, 'year');
};

export const checkOptionRules = (group: Group, option: ResourceOption, booking: BookingData, room?: RoomData, traveller?: GuestData, roomPartyNight?: Moment|null, groupOption?: GroupResourceOption) => {
    //check if admin_edited.
    if (room) {
        if (room.optionValues.filter(roomOption => roomOption.optionId === option.id && roomOption.adminEdited).length > 0) {
            return true;
        }
        if (traveller && room.guestsData.filter(guestData => traveller.id === guestData.id && guestData.optionValues.filter(roomOption => roomOption.optionId === option.id && roomOption.adminEdited).length > 0).length > 0) {
            return true;
        }
    }

    //if available check conditions.
    const pass = option.conditions!.reduce((accum: boolean, optionCondition) => {
        if (!accum) return false;

        const datatype = conditions.find(condition => condition.type === optionCondition.attribute_type)!
        const conditionValue = datatype.cast(optionCondition.attribute_value as string);
        let testFunction = datatype.conditions.find(condition => condition.value === optionCondition.condition)!.test;
        let testValue;

        switch (optionCondition.type) {
            case ResourceOptionConditionType.TRAVELLER:
                //If traveller isnt set always return true to get a full list of available traveller options.
                if (!traveller) return true;
                switch (optionCondition.attribute) {
                    case 'age':
                        testValue = getMemberAgeAtEvent(booking, traveller);
                        break;
                    case 'tour_role':
                        testValue = traveller!.tour_role;
                        break;
                    case 'name':
                        testValue = `${traveller!.first_name} ${traveller!.last_name}`;
                        break;
                    case 'has_option_with_name':
                        testValue = traveller!.optionValues;
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) !== undefined;
                        break;
                    case 'has_no_option_with_name':
                        testValue = traveller!.optionValues;
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) === undefined;
                        break;
                    case 'room_guest_number':
                        testValue = traveller!.roomGuestNumber;
                        break;
                    case 'room_adult_number':
                        const adults = room!.guestsData
                            .filter(guestData => getMemberAgeAtEvent(booking, guestData) >= 18)
                            .sort((a, b) => a.roomGuestNumber - b.roomGuestNumber);
                        const index = adults.indexOf(traveller);
                        testValue = index !== -1 ? index + 1 : 0;
                        break;
                    case 'gender':
                    case 'nationality':
                    case 'shoe_category':
                    case 'shoe_size':
                    case 'pants_size':
                    case 'shirt_size':
                    case 'tie_size':
                        //console.warn(`Travellers do not have ${optionCondition.attribute} yet.`);
                        break;
                }
                break;
            case ResourceOptionConditionType.ROOM:
                switch (optionCondition.attribute) {
                    case 'num_room_occupants':
                        testValue = room!.guestsData.length;
                        break;
                    case 'start_date':
                        testValue = booking.nights.start;
                        break;
                    case 'end_date':
                        testValue = booking.nights.end;
                        break;
                    case 'num_days':
                        testValue = booking.nights.end.diff(booking.nights.start, 'day');
                        break;
                    case 'has_option_with_name':
                        testValue = room!.optionValues;
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) !== undefined;
                        break;
                    case 'has_no_option_with_name':
                        testValue = room!.optionValues;
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) === undefined;
                        break;
                    case 'hotel_name':
                        testValue = room!.hotelName;
                        break;
                    case 'has_occupant_option_with_name':
                        testValue = _.flatten(room!.guestsData.map(guestData => guestData.optionValues));
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) !== undefined;
                        break;
                    case 'has_no_occupant_option_with_name':
                        testValue = _.flatten(room!.guestsData.map(guestData => guestData.optionValues));
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) === undefined;
                        break;
                }
                break;
            case ResourceOptionConditionType.BOOKING:
                switch (optionCondition.attribute) {
                    case 'num_travellers':
                        testValue = booking.roomsData.reduce((newValue, roomData) => newValue + roomData.guestsData.length, 0);
                        break;
                    case 'start_date':
                        testValue = booking.nights.start;
                        break;
                    case 'end_date':
                        testValue = booking.nights.end;
                        break;
                    case 'num_days':
                        testValue = booking.nights.end.diff(booking.nights.start, 'day');
                        break;
                    case 'has_option_with_name':
                        testValue = _.flatten(booking.roomsData.map(roomData => roomData.optionValues));
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) !== undefined;
                        break;
                    case 'has_no_option_with_name':
                        testValue = _.flatten(booking.roomsData.map(roomData => roomData.optionValues));
                        testFunction = (options: OptionValue[], name: string) => options.find(testOption => testOption.groupResourceOption.option.internal_name === name && testOption.value > 0) === undefined;
                        break;
                }
                break;
            case ResourceOptionConditionType.ROOM_PARTY_NIGHT:
                if (!roomPartyNight) return false;
                switch (optionCondition.attribute) {
                    case 'num_room_occupants':
                        testValue = room!.guestsData.length;
                        break;
                    case 'extra_night':
                        testValue = roomPartyNight!.isBefore(booking.nights.mandatoryStart) || roomPartyNight!.isSameOrAfter(booking.nights.mandatoryEnd);
                        break;
                }
                break;
            case ResourceOptionConditionType.ADMIN:
                switch (optionCondition.attribute) {
                    case 'is_admin_only':
                        // if the group level option is mandatory we want it to be returned in the list of mandatory options
                        testValue = groupOption ? groupOption.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_MANDATORY : false;
                        break;
                }
                break;
        }

        if (!testFunction || !conditionValue) return false;
        return testFunction(testValue, conditionValue);
    }, true);

    return pass;
};

export const getGroupNightsData = (group: Group) => {
    const mandatoryStart = moment(new Date(group.start_date!));
    const mandatoryEnd = moment(new Date(group.end_date!));
    const mandatoryNightsCount = mandatoryEnd.diff(mandatoryStart, 'days');
    const maxAdditionalNights = group.max_additional_nights!;
    const availableNightsStart = group.start_date_additional ? moment(new Date(group.start_date_additional!)) : mandatoryStart.clone();//mandatoryStart.clone().subtract(maxAdditionalNights, 'days');
    const availableNightsEnd = group.end_date_additional ? moment(new Date(group.end_date_additional!)) : mandatoryEnd.clone();//mandatoryEnd.clone().add(maxAdditionalNights, 'days');
    const availableNightsCount = availableNightsEnd.diff(availableNightsStart, 'days');
    const totalNightsAllowed = mandatoryNightsCount + maxAdditionalNights;

    return {
        mandatoryStart,
        mandatoryEnd,
        mandatoryNightsCount,
        maxAdditionalNights,
        availableNightsStart,
        availableNightsEnd,
        availableNightsCount,
        totalNightsAllowed,
    }
};

// export const logGroupMemberCosts = (group:Group, booking:BookingData) => {
//     booking.roomsData.map(room => room.guestsData.map(guest => {
//         console.log(getTotalGuestCost(group, booking, guest));
//         guest.optionValues.map(optionValue => {
//             const groupOption = group.options!.find(checkOption => optionValue.optionId === checkOption.option.id)!;
//             console.log(groupOption.option.internal_name, '$'+groupOption.unitCost, ' x ', optionValue.value);
//         });
//     }));
// }

//converstion functions
export const convertGroupBookingToBookingData = (newGroup: Group, groupBooking: GroupBooking, setGroup: (group: Group) => void): BookingData => {
    const group = {...newGroup};
    const billingPartiesData = groupBooking.billingParties ? groupBooking.billingParties.map(billingParty => convertBillingPartyToBillingPartyData(billingParty)) : [];
    const roomsData = groupBooking.roomParties ? groupBooking.roomParties.map(roomParty => convertRoomPartyToRoomData(group, roomParty)) : [];
    setGroup(group);

    return {
        id: groupBooking.id,
        roomsData,
        billingPartiesData,
        nights: {
            start: moment(groupBooking.start_date).clone(),
            end: moment(groupBooking.end_date).clone(),
            mandatoryStart: moment(group.start_date).clone(),
            mandatoryEnd: moment(group.end_date).clone(),
        },
        validData: [true, true, true, true, true, true],
    };
};

const convertBillingPartyToBillingPartyData = (billingParty: BillingParty): BillingPartyData => {
    const primaryMember = billingParty.payingMembers!.find(member => member.primary_paying_member)!;

    return {
        id: '' + billingParty.id,
        first_name: '' + primaryMember.first_name,
        last_name: '' + primaryMember.last_name,
        email: '' + primaryMember.email,
        address_line_1: '' + primaryMember.address_line_1,
        address_line_2: '' + primaryMember.address_line_2,
        city: '' + primaryMember.city,
        state: '' + primaryMember.state,
        zipcode: '' + primaryMember.zipcode,
        country: '' + primaryMember.country,
        primary_phone: '' + primaryMember.primary_phone,
        primary_phone_code: '' + primaryMember.primary_phone_code,
        secondary_phone: '' + primaryMember.secondary_phone,
        secondary_phone_code: '' + primaryMember.secondary_phone_code,
        isValid: true,
        onlyParty: false,
    };
};

const convertRoomPartyToRoomData = (group: Group, roomParty: RoomParty): RoomData => {

    //add consumed room resource back into resource bank.
    if(roomParty && roomParty.roomResource && roomParty.roomResource.room_resource_id){
        const roomResource = group.hotel!.roomResources!.find(checkResource => checkResource.id === roomParty.roomResource!.room_resource_id);
        if(roomResource){
            const start = moment(new Date(roomParty.start_date!));
            const end = moment(new Date(roomParty.end_date!));

            roomResource.resource_banks!
                .filter(item => {
                    const bankDate = moment(item.date);
                    return bankDate.isSameOrAfter(start) && bankDate.isBefore(end);
                })
                .map(item => {
                    item.remaining!+=1;
                });
        }
    }

    return {
        id: '' + roomParty.id,
        wheelchair: roomParty.wheelchair_access!,
        hasInfants: roomParty.has_infants!,
        notes: '' + roomParty.notes!,
        isValid: true,
        guestsData: roomParty.members ? roomParty.members.map(member => convertRoomMemberToGuestData(group, member)) : [],
        optionValues: roomParty.options ? roomParty.options.map(option => convertRoomOptionToOptionValue(group, option)).filter(option => option.id !== -1) : [],
        hotelName: roomParty.hotel!.internal_name!,
    };
};

const convertRoomOptionToOptionValue = (group: Group, roomPartyOption: RoomPartyOption): OptionValue => {
    const groupResourceOption = group.options!.find(option => option.id === roomPartyOption.groupResourceOption!.id)!;
    if (!groupResourceOption) {
        console.log('group resource option not found for room party option.', roomPartyOption);
        // @ts-ignore
        return {id: -1, optionId: -1, value: 0, groupResourceOption: null};
    } else {
        return {
            id: roomPartyOption.id!,
            optionId: groupResourceOption.option!.id!,
            value: roomPartyOption.unit_count,
            adminEdited: roomPartyOption.admin_edited,
            groupResourceOption,
        }
    }
}

const convertRoomMemberToGuestData = (group: Group, roomPartyMember: RoomPartyMember): GuestData => {
    return {
        id: '' + roomPartyMember.id,
        roomId: '' + roomPartyMember.room_party_id,
        billingPartyId: '' + roomPartyMember.billing_party_id,
        first_name: '' + roomPartyMember.groupMember!.first_name!,
        last_name: '' + roomPartyMember.groupMember!.last_name!,
        dob: moment(new Date(roomPartyMember.groupMember!.dob!)).format('YYYY/MM/DD'),
        tour_role: roomPartyMember.groupMember!.tour_role!,
        primary_occupant: roomPartyMember.primary_occupant!,
        roomGuestNumber: roomPartyMember.guest_number!,
        isValid: true,
        optionValues: roomPartyMember.options ? roomPartyMember.options.map(option => convertRoomMemberOptionToOptionValue(group, option)).filter(option => option.id !== -1) : [],
    };
};

const convertRoomMemberOptionToOptionValue = (group: Group, roomPartyMemberOption: RoomPartyMemberOption): OptionValue => {
    const groupResourceOption = group.options!.find(option => option.id === roomPartyMemberOption.groupResourceOption!.id);
    if (!groupResourceOption) {
        console.log('group resource option not found for room member option.', roomPartyMemberOption);
        // @ts-ignore
        return {id: -1, optionId: -1, value: 0, groupResourceOption: null};
    } else {
        return {
            id: roomPartyMemberOption.id!,
            optionId: groupResourceOption.option!.id!,
            value: roomPartyMemberOption.unit_count,
            adminEdited: roomPartyMemberOption.admin_edited,
            groupResourceOption,
        };
    }
};
